import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { FormBuilder, FormControl, ValidatorFn } from "@angular/forms";
import { DynamicFormService } from "./dynamic-form.service";
import { ColumnDto, EditableConfig } from "../models/table-meta.interface";
import { GridApi, ICellEditorParams } from "ag-grid-community";

@Injectable({
  providedIn: "root"
})
export class GridTableService {
  private gridState: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private errorsMap = new Map<string, string[]>();
  private resolveColumnDto: (colId: string) => ColumnDto | undefined = () => undefined;

  gridApi!: GridApi;

  constructor(private dynamicFormService: DynamicFormService,
              private fb: FormBuilder) {
  }

  getGridState$(): Observable<any> {
    return this.gridState.asObservable();
  }

  saveGridState(value: any) {
    this.gridState.next(value);
  }

  clearGridState() {
    this.gridState.next(null);
  }

  validateEditableGrid(columnDto: ColumnDto, value: any): string[] | null {
    const validators = columnDto?.editableConfig?.validators || [];
    const validatorFns = this.dynamicFormService.getValidators(validators, this.fb.group({}));

    const ctrl = new FormControl(value, validatorFns);

    if (!ctrl.errors) return null;

    return Object.keys(ctrl.errors)
      .map(errKey => {
        const config = validators.find(v => v.type === errKey);
        return config?.message || errKey;
      })
      .filter(Boolean);
  }

  commitAndRefocusCell(params: any) {
    if(!params) return
    const { api, rowIndex, column } = params;
    const colId = column.getColId();

    params.stopEditing?.();

    // Hand focus back to the cell once the editor is gone
    setTimeout(() => {
      api?.ensureIndexVisible(rowIndex);
      api?.setFocusedCell(rowIndex, colId);
    }, 0);
  }

  gridInit(gridApi: GridApi, resolveColumnDto: (colId: string) => ColumnDto | undefined) {
    this.errorsMap.clear();
    this.gridApi = gridApi;
    this.resolveColumnDto = resolveColumnDto;

  }

  private keyFor(row: any, colId: string): string {
    const rowId = JSON.stringify(row);
    return `${rowId}||${colId}`;
  }

  validateAllCells() {
    if (!this.gridApi) return true;

    this.errorsMap.clear();

    this.gridApi.stopEditing(); //stop editing of any existing cells

    const columns = this.gridApi.getColumns() ?? [];
    const dataCols = columns.map(c => c.getColDef()).filter(cd => !!cd.field);

    this.gridApi.forEachNode((node) => {
      const row = node.data;
      for (const cd of dataCols) {
        const colId = cd.field!;
        const columnDto = this.resolveColumnDto(colId);
        if (!columnDto) continue;

        // Resolve string-reference validator params (e.g. { type: "max", params: "baseAmount" })
        // into the actual row value — mirrors what AgGridWrapperComponent does for cell editors.
        const resolvedColumnDto = this.resolveValidatorParams(columnDto, row);
        const value = this.gridApi!.getCellValue({ rowNode: node, colKey: colId });
        const errs = this.validateEditableGrid(resolvedColumnDto, value) || [];
        if (errs.length) {
          this.errorsMap.set(this.keyFor(row, colId), errs);
        }
      }
    });

    this.gridApi.refreshCells({ force: true });
    return this.errorsMap.size === 0;
  }

  /**
   * Resolve validator params that reference another column (e.g. min/max with params: "baseAmount")
   * into the row's actual value. pattern's params is a literal regex string and is left untouched.
   */
  resolveValidatorParams(columnDto: ColumnDto, rowData: any): ColumnDto {
    const isFieldRef = (v: { type: string; params?: any }) =>
      typeof v.params === 'string' && v.type !== 'pattern';
    const hasStringParams = columnDto.editableConfig?.validators?.some(isFieldRef);
    if (!hasStringParams) return columnDto;

    return {
      ...columnDto,
      editableConfig: {
        ...columnDto.editableConfig,
        validators: columnDto.editableConfig!.validators!.map(v =>
          isFieldRef(v) ? { ...v, params: rowData?.[v.params] } : v
        ),
      } as EditableConfig,
    };
  }

  focusFirstError() {
    if (!this.gridApi || this.errorsMap.size === 0) return;

    const [firstKey] = this.errorsMap.keys();
    const [rowId, colId] = firstKey.split("||");

    let rowIndex: number | null = null;
    this.gridApi.forEachNode((n) => {
      const nid = JSON.stringify(n.data);
      if (nid === rowId) {
        rowIndex = n.rowIndex!;
      }
    });
    if (rowIndex == null) return;

    this.gridApi.stopEditing();
    this.gridApi.ensureIndexVisible(rowIndex);
    this.gridApi.setFocusedCell(rowIndex, colId);
    this.gridApi.startEditingCell({ rowIndex, colKey: colId });
  }

  /** Adds/removes a "required" validator on the given fields’ ColumnDto.
   *  We tag the validator with `source` so we can remove it later
   *  without touching other required rules that might exist.
   */
  toggleRequiredOnColumns(fieldNames: string[], makeRequired: boolean) {
    fieldNames.forEach((field) => {
      const col = this.gridApi?.getColumn(field);
      if (!col) return;

      const colDef = col.getColDef();
      const columnDto: ColumnDto | undefined = colDef.cellEditorParams?.col;
      if (!columnDto) return;

      columnDto.editableConfig = columnDto.editableConfig || {} as EditableConfig;
      const validators = columnDto.editableConfig.validators || [];

      const idx = validators.findIndex((v: any) => v.type === "required");
      if (makeRequired) {
        if (idx === -1) {
          validators.push({
            type: "required",
            message: `${columnDto.displayName ?? field} is required`,
          });
        }
      } else {
        if (idx > -1) validators.splice(idx, 1);
      }

      columnDto.editableConfig.validators = validators;
      colDef.cellRendererParams.columnDto = columnDto;
    });

    // this.gridApi?.refreshHeader();
    // this.gridApi?.refreshCells({ columns: fieldNames, force: true });
  }
}
